import { FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import vouchersApi from '../vouchers/api';
import { Context as UserPreferencesContext } from '../userPreferences/context';
import { Context as AuthContext } from '../auth/context';
import {
    AUDIT_TOKENS,
    DEFAULT_TOKENS,
    DesignTokens,
    UI_SCALE_DELTA,
    UiScale,
    mergeTokens,
    tokensToCss
} from '../../styles/designTokens';

import Context from './context';

const APP_CONFIG_KEY = 'design_tokens';
const DRAFT_KEY = 'taytan-tokens-draft';
const AUDIT_KEY = 'taytan-tokens-audit';
const STYLE_ID = 'taytan-design-tokens';

function readSession<T>(key: string): T | null {
    try {
        const raw = sessionStorage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : null;
    } catch {
        return null;
    }
}

function writeSession(key: string, value: unknown) {
    try {
        if (value === null || value === undefined) sessionStorage.removeItem(key);
        else sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
        /* private browsing / quota — the draft is a convenience, never load-bearing */
    }
}

/**
 * Resolves the design tokens in force and publishes them as CSS custom
 * properties.
 *
 * Layering, last wins:
 *   DEFAULT_TOKENS  →  app_config['design_tokens']  →  user scale preset  →  session draft
 *
 * The draft wins so an administrator can edit on the Appearance page and then
 * walk other screens to see the effect before saving. It lives in
 * sessionStorage rather than localStorage so a forgotten preview cannot survive
 * a browser restart and turn into a mystery support ticket.
 *
 * Must be mounted inside the api providers (it reads user preferences) and
 * outside ThemesProvider (which consumes the resolved tokens).
 */
const DesignTokensProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [tenantTokens, setTenantTokens] = useState<Partial<DesignTokens> | null>(null);
    const [draft, setDraftState] = useState<Partial<DesignTokens> | null>(() =>
        readSession<Partial<DesignTokens>>(DRAFT_KEY)
    );
    const [auditMode, setAuditModeState] = useState<boolean>(() => readSession<boolean>(AUDIT_KEY) === true);

    const { preferences } = useContext(UserPreferencesContext) as { preferences?: { uiScale?: UiScale } };
    const scale: UiScale = preferences?.uiScale || 'default';

    const { currUser } = useContext(AuthContext);

    // Tenant default. Only once authenticated — the endpoint 401s otherwise and
    // the login screen has no need for it. A miss is normal (no row until an
    // admin saves one), so a failure here must never block rendering.
    useEffect(() => {
        if (!currUser) return undefined;
        let cancelled = false;
        vouchersApi
            .getAppConfig(APP_CONFIG_KEY)
            .then((res: any) => {
                if (cancelled) return;
                const raw = res?.value ?? res;
                if (!raw) return;
                setTenantTokens(typeof raw === 'string' ? JSON.parse(raw) : raw);
            })
            .catch(() => {
                /* no tenant override configured */
            });
        return () => {
            cancelled = true;
        };
    }, [currUser]);

    const tokens = useMemo<DesignTokens>(() => {
        if (auditMode) return AUDIT_TOKENS;
        return mergeTokens(DEFAULT_TOKENS, tenantTokens, UI_SCALE_DELTA[scale], draft);
    }, [tenantTokens, scale, draft, auditMode]);

    // What "Save as company default" writes: the same stack WITHOUT the editing
    // admin's personal scale. Saving `tokens` instead would bake whichever
    // preset that one account happens to run — an admin on compact would ship
    // compact's control heights to the whole tenant, with nothing on screen
    // saying so.
    const savableTokens = useMemo<DesignTokens>(
        () => mergeTokens(DEFAULT_TOKENS, tenantTokens, draft),
        [tenantTokens, draft]
    );

    // Publish as a stylesheet, NOT as inline style on the root element: an
    // inline style out-specifies `body[data-theme="dark"]` in base.scss and
    // would freeze every colour at its light value in dark mode. Appended to
    // <head> after base.scss, so equal specificity resolves by document order.
    useEffect(() => {
        let styleEl = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
        if (!styleEl) {
            styleEl = document.createElement('style');
            styleEl.id = STYLE_ID;
            document.head.appendChild(styleEl);
        }
        styleEl.textContent = tokensToCss(tokens);
    }, [tokens]);

    const setDraft = useCallback((patch: Partial<DesignTokens> | null) => {
        setDraftState((prev) => {
            const next = patch === null ? null : { ...(prev || {}), ...patch };
            writeSession(DRAFT_KEY, next);
            return next;
        });
    }, []);

    const setAuditMode = useCallback((on: boolean) => {
        setAuditModeState(on);
        writeSession(AUDIT_KEY, on ? true : null);
    }, []);

    const saveTenantDefault = useCallback(async (next: Partial<DesignTokens>) => {
        await vouchersApi.setAppConfig(APP_CONFIG_KEY, JSON.stringify(next));
        setTenantTokens(next);
        setDraftState(null);
        writeSession(DRAFT_KEY, null);
    }, []);

    // Persist null rather than a snapshot of today's defaults — a snapshot
    // would pin this tenant to the current values forever and it would never
    // pick up a future change to what we ship.
    const resetTenantDefault = useCallback(async () => {
        await vouchersApi.setAppConfig(APP_CONFIG_KEY, '');
        setTenantTokens(null);
        setDraftState(null);
        writeSession(DRAFT_KEY, null);
    }, []);

    const value = useMemo(
        () => ({
            tokens,
            savableTokens,
            tenantTokens,
            draft,
            auditMode,
            setDraft,
            saveTenantDefault,
            resetTenantDefault,
            setAuditMode,
            scale
        }),
        [
            tokens,
            savableTokens,
            tenantTokens,
            draft,
            auditMode,
            setDraft,
            saveTenantDefault,
            resetTenantDefault,
            setAuditMode,
            scale
        ]
    );

    return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default DesignTokensProvider;
